import { useState } from 'react';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Paperclip } from 'lucide-react';
import { getTicket, replyTicket } from '@mkcore/portal/schema';
import { Button, Card, ErrorState, Field, Skeleton, Textarea } from '@mkcore/ui';
import { call } from '@/lib/api';
import { formatDateTime } from '@/lib/format';
import { useErrorMessage } from '@/lib/mutation';
import { FilePicker, TicketState, uploadFile } from '@/features/portal/tickets';

/** A ticket of the client (CO-0660, CO-0661): the conversation, files, and an answer. */
export const Route = createFileRoute('/portal/_client/tickets/$id')({
  component: Ticket,
});

function Ticket() {
  const { t } = useTranslation();
  const { id } = Route.useParams();
  const qc = useQueryClient();
  const message = useErrorMessage();
  const q = useQuery({ queryKey: ['portal', 'ticket', id], queryFn: () => call(getTicket, { id }) });
  const [body, setBody] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (q.isError) return <ErrorState title={t('portal.tickets.notFound')} description={message(q.error)} />;
  if (!q.data) return <Skeleton className="h-64" />;
  const x = q.data;
  const reply = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const fileIds = [];
      for (const f of files) fileIds.push(await uploadFile(f));
      const after = await call(replyTicket, { id, body: body.trim(), fileIds });
      qc.setQueryData(['portal', 'ticket', id], after);
      await qc.invalidateQueries({ queryKey: ['portal', 'tickets'] });
      setBody('');
      setFiles([]);
    } catch (err) {
      setError(message(err));
    }
    setBusy(false);
  };
  return (
    <>
      <Link to="/portal/tickets" className="flex w-fit items-center gap-1 py-1 text-sm text-fg-2 hover:underline">
        <ArrowLeft className="size-4" aria-hidden /> {t('portal.modules.tickets')}
      </Link>
      <div className="grid gap-2">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-2xl font-bold">
            <span className="text-fg-3 tabular">{x.key}</span> {x.subject}
          </h1>
          <TicketState status={x.status} />
        </div>
        <p className="text-sm text-fg-2">
          {[t(`portal.tickets.types.${x.type}`), t(`portal.tickets.priorities.${x.priority}`), x.projectName, t('portal.tickets.reported', { at: formatDateTime(x.createdAt) })].filter(Boolean).join(' · ')}
        </p>
        {x.status === 'waiting' && (
          <p role="status" className="rounded-md bg-warning-bg px-3 py-2 text-sm">
            {t('portal.tickets.waitingForYou')}
          </p>
        )}
      </div>
      <ol className="grid gap-3" aria-label={t('portal.tickets.conversation')}>
        {x.messages.map((m) => (
          <li key={m.id}>
            <Card className={`grid gap-2 p-4 ${m.fromClient ? '' : 'border-l-4 border-l-gold'}`}>
              <p className="text-xs text-fg-2">
                <span className="font-bold text-fg">{m.author}</span> · {formatDateTime(m.at)}
              </p>
              <div className="whitespace-pre-wrap break-words text-sm">{m.body}</div>
              {m.files.length > 0 && (
                <ul className="flex flex-wrap gap-2 text-sm">
                  {m.files.map((f) => (
                    <li key={f.fileId}>
                      <a href={`/api/v1/portal/tickets/${x.id}/files/${f.fileId}?download=1`} className="flex items-center gap-1 underline">
                        <Paperclip className="size-4" aria-hidden /> {f.name}
                      </a>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          </li>
        ))}
      </ol>
      {x.status === 'closed' ? (
        <p className="text-sm text-fg-2">{t('portal.tickets.closedHint')}</p>
      ) : (
        <Card className="p-4">
          <form className="grid gap-3" onSubmit={(e) => void reply(e)}>
            <Field label={t('portal.tickets.answer')}>{(p) => <Textarea {...p} required rows={5} value={body} onChange={(e) => setBody(e.target.value)} />}</Field>
            <FilePicker id="reply-files" files={files} onChange={setFiles} />
            {error && (
              <p role="alert" className="text-sm text-danger">
                {error}
              </p>
            )}
            <Button type="submit" className="w-fit" loading={busy} disabled={!body.trim()}>
              {t('portal.tickets.sendAnswer')}
            </Button>
          </form>
        </Card>
      )}
    </>
  );
}
